import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const messages = {
  lifecycle_unsupported_venue: "Это направление требует отдельного безопасного отзыва публикации. Материал не изменён.",
  lifecycle_protected_material: "Служебные MASTER и корневое вступление защищены от этого действия.",
  lifecycle_revision_conflict: "Материал изменился. Закрой окно и обнови список перед повтором.",
  lifecycle_operation_conflict: "Другая операция уже выполняется. Обнови список и продолжи её.",
  lifecycle_cleanup_pending: "Читательский доступ закрыт. Очистка не завершена. Повтори подтверждение, чтобы продолжить.",
  lifecycle_foreign_reference: "Найдена противоречивая связь с другим материалом. Действие остановлено без изменений.",
  lifecycle_foreign_page: "Страница связана с другим материалом. Действие остановлено без изменений.",
  lifecycle_archive_requires_unpublish: "Сначала снимите материал с публикации. История сохранится.",
  lifecycle_confirmation_required: "Для этого действия нужно подтверждение.",
  lifecycle_action_invalid: "Недопустимое действие. Материал не изменён.",
  lifecycle_restore_not_archived: "Восстановить можно только материал из архива.",
};

export default function OwnerMaterialLifecycleActions({ material, onDone, openLibrary = false }) {
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = `/admin/editorial/materials/${encodeURIComponent(material.id)}/owner-lifecycle`;
  const explain = error => messages[error?.response?.data?.detail?.code] || "Не удалось завершить действие. Попробуй ещё раз.";
  const open = async action => {
    setDialog({ action }); setBusy(true);
    try {
      const { data } = await api.get(base);
      if (data.deleted) { setDialog(null); await onDone(); return; }
      if (data.operation && data.operation !== action) {
        setDialog({ action, error: "Сначала продолжите уже начатое действие с этим материалом." }); return;
      }
      if (openLibrary && (
        (action === "unpublish" && data.supports_unpublish !== true)
        || (action === "archive" && data.supports_archive !== true)
        || (action === "restore" && data.supports_restore !== true)
      )) {
        setDialog({ action, error: "Действие недоступно для текущей версии материала. Обновите список." }); return;
      }
      setDialog({ action, title: data.title || material.title, request: {
        operation_id: data.operation_id || crypto.randomUUID(),
        expected_draft_revision: data.draft_revision, confirmed: true,
      }});
    } catch (error) { setDialog({ action, error: explain(error) }); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    if (!dialog?.request || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/${dialog.action}`, dialog.request);
      const expected = { delete: "deleted", unpublish: "unpublished", archive: "archived", restore: "restored" }[dialog.action];
      if (data?.ok !== true || data.action !== expected) throw new Error("not confirmed");
      toast.success(dialog.action === "delete" ? "Материал удалён"
        : dialog.action === "archive" ? "Материал перемещён в архив"
          : dialog.action === "restore" ? "Материал восстановлен как черновик"
          : "Материал снят с публикации");
      setDialog(null); await onDone();
    } catch (error) { setDialog(current => ({ ...current, error: explain(error) })); }
    finally { setBusy(false); }
  };
  return <>
    <div className="flex shrink-0 flex-wrap gap-1 sm:flex-col sm:justify-center" data-testid={`owner-material-actions-${material.id}`}>
      {openLibrary ? (
        material.status === "published"
          ? <Button type="button" size="sm" variant="outline" onClick={() => open("unpublish")}>Снять с публикации</Button>
          : material.status === "archived"
            ? <Button type="button" size="sm" variant="outline" onClick={() => open("restore")}>Восстановить в черновики</Button>
            : <Button type="button" size="sm" variant="outline" onClick={() => open("archive")}>В архив</Button>
      ) : (
        <>
          <Button type="button" size="sm" variant="outline" onClick={() => open("unpublish")}>Снять с публикации</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => open("delete")}>Удалить материал</Button>
        </>
      )}
    </div>
    <Dialog open={!!dialog} onOpenChange={value => { if (!value && !busy) setDialog(null); }}>
      <DialogContent data-testid="owner-material-lifecycle-dialog">
        <DialogHeader>
          <DialogTitle>{dialog?.action === "delete" ? "Удалить материал?"
            : dialog?.action === "archive" ? "Переместить в архив?"
            : dialog?.action === "restore" ? "Восстановить черновик?" : "Снять с публикации?"}</DialogTitle>
          <DialogDescription>
            «{dialog?.title || material.title}». {dialog?.action === "delete"
              ? "Все связанные публикации в Томе будут отозваны. Материал, развороты и сохранённые версии будут удалены без восстановления. Общие файлы медиатеки и доступы к продукту сохранятся."
              : dialog?.action === "archive"
                ? "Материал исчезнет из рабочего списка и публичной библиотеки. История публикаций, утверждённые версии и общие фотографии сохранятся. Это не безвозвратное удаление."
                : dialog?.action === "restore"
                  ? "Материал вернётся в черновики для редактирования. Он не будет опубликован автоматически; история и фотографии сохранятся."
                : openLibrary
                  ? "Читатели больше не увидят публикацию в Открытой библиотеке. Материал останется черновиком, который можно исправить или затем перенести в архив."
                  : "Читатели больше не увидят материал в Томе, содержании и поиске. Исходный материал и утверждённые версии останутся для редактирования и новой публикации."}
          </DialogDescription>
        </DialogHeader>
        {dialog?.error && <p role="alert" className="text-sm text-red-700">{dialog.error}</p>}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={() => setDialog(null)}>Отмена</Button>
          <Button type="button" disabled={busy || !dialog?.request} onClick={confirm} data-testid="owner-material-lifecycle-confirm">
            {busy ? "Сохранение…" : dialog?.action === "delete" ? "Подтверждаю удаление"
              : dialog?.action === "archive" ? "Переместить в архив"
              : dialog?.action === "restore" ? "Восстановить в черновики" : "Снять с публикации"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
